import { useEffect, useRef, useState } from "react";
import { Lock, ExternalLink, Navigation } from "lucide-react";
import {
  mapboxgl,
  MAP_STYLE,
  NEON,
  INK,
  MAPBOX_TOKEN,
  circlePolygon,
} from "@/lib/mapbox";

/**
 * EventMap — editorial light Mapbox map for the event detail page.
 * - canSeeExact=false: soft cream map with a gentle lime privacy halo + rough_area label.
 * - canSeeExact=true:  bounce-in chunky cream pin with thick ink outline + lime active dot.
 */
const ROUGH_RADIUS_M = 500;
const LOCKED_ZOOM = 13.2;
const EXACT_ZOOM = 15.4;

export default function EventMap({ event, canSeeExact }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const lat = event?.latitude;
  const lng = event?.longitude;
  const hasCoords = lat != null && lng != null;

  // Initialise the map once we have coords.
  useEffect(() => {
    if (!containerRef.current || !hasCoords || !MAPBOX_TOKEN) return;
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: [lng, lat],
      zoom: canSeeExact ? EXACT_ZOOM : LOCKED_ZOOM,
      attributionControl: false,
      cooperativeGestures: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      doubleClickZoom: !!canSeeExact,
      scrollZoom: !!canSeeExact,
      dragPan: !!canSeeExact,
      touchZoomRotate: !!canSeeExact,
    });
    mapRef.current = map;
    if (canSeeExact) {
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    }
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");

    // 🔧 Defensive canvas resize — see MapPage.jsx for full rationale.
    const safeResize = () => { try { map.resize(); } catch { /* noop */ } };
    requestAnimationFrame(safeResize);
    const t1 = setTimeout(safeResize, 100);
    const t2 = setTimeout(safeResize, 500);
    map.on("style.load", safeResize);
    map.on("load", safeResize);
    const ro = new ResizeObserver(safeResize);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", safeResize);
    const cleanupResize = () => {
      clearTimeout(t1);
      clearTimeout(t2);
      try { ro.disconnect(); } catch { /* noop */ }
      window.removeEventListener("resize", safeResize);
    };

    map.on("load", () => {
      // Rough halo for locked view (lime fill + dashed ink ring).
      map.addSource("rough-circle", {
        type: "geojson",
        data: circlePolygon(lng, lat, ROUGH_RADIUS_M),
      });
      map.addLayer({
        id: "rough-circle-fill",
        type: "fill",
        source: "rough-circle",
        paint: {
          "fill-color": NEON,
          "fill-opacity": canSeeExact ? 0 : 0.28,
        },
      });
      map.addLayer({
        id: "rough-circle-line",
        type: "line",
        source: "rough-circle",
        paint: {
          "line-color": INK,
          "line-width": 2.5,
          "line-dasharray": [3, 2],
          "line-opacity": canSeeExact ? 0 : 0.85,
        },
      });
      setReady(true);
    });

    return () => {
      cleanupResize();
      try { map.remove(); } catch { /* noop */ }
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCoords, lat, lng]);

  // When canSeeExact flips on (or starts on), reveal the editorial pin.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !hasCoords) return;
    if (canSeeExact) {
      if (map.getLayer("rough-circle-fill"))
        map.setPaintProperty("rough-circle-fill", "fill-opacity", 0);
      if (map.getLayer("rough-circle-line"))
        map.setPaintProperty("rough-circle-line", "line-opacity", 0);

      if (!markerRef.current) {
        const el = document.createElement("div");
        el.className = "inonit-marker";
        el.innerHTML = `<span class="dot"></span><span class="ring"></span>`;
        markerRef.current = new mapboxgl.Marker({ element: el, anchor: "bottom" })
          .setLngLat([lng, lat])
          .addTo(map);
      }
      map.flyTo({ center: [lng, lat], zoom: EXACT_ZOOM, duration: 1200, essential: true });

      map.scrollZoom.enable();
      map.dragPan.enable();
      map.touchZoomRotate.enable();
      map.doubleClickZoom.enable();

      requestAnimationFrame(() => setRevealed(true));
    } else {
      setRevealed(false);
      if (markerRef.current) {
        try { markerRef.current.remove(); } catch { /* noop */ }
        markerRef.current = null;
      }
      if (map.getLayer("rough-circle-fill"))
        map.setPaintProperty("rough-circle-fill", "fill-opacity", 0.28);
      if (map.getLayer("rough-circle-line"))
        map.setPaintProperty("rough-circle-line", "line-opacity", 0.85);
      map.flyTo({ center: [lng, lat], zoom: LOCKED_ZOOM, duration: 800, essential: true });
      map.scrollZoom.disable();
      map.dragPan.disable();
      map.touchZoomRotate.disable();
      map.doubleClickZoom.disable();
    }
  }, [canSeeExact, ready, hasCoords, lat, lng]);

  if (!hasCoords) return null;
  if (!MAPBOX_TOKEN) {
    return (
      <div className="border-[3px] border-ink rounded-2xl bg-cream h-56 flex items-center justify-center text-ink/50 text-xs uppercase tracking-widest">
        Map unavailable — token missing.
      </div>
    );
  }

  const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

  return (
    <div
      data-testid={canSeeExact ? "event-map-exact" : "event-map-locked"}
      className="flex flex-col gap-3"
    >
      <div className="relative border-[3px] border-ink rounded-[1.5rem] h-60 sm:h-72 overflow-hidden bg-cream">
        <div
          ref={containerRef}
          className={`w-full h-full transition-opacity duration-500 ${
            ready ? "opacity-100" : "opacity-0"
          } ${canSeeExact ? "" : "blur-[1.5px] saturate-[0.92]"}`}
        />
        {!ready && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-[linear-gradient(110deg,rgba(10,10,10,0.03),rgba(10,10,10,0.07),rgba(10,10,10,0.03))] bg-[length:200%_100%] animate-shimmer-em" />
          </div>
        )}
        {!canSeeExact && (
          <div className="pointer-events-none absolute inset-0 flex items-end justify-center p-4">
            <div className="inline-flex flex-col items-center gap-2 bg-paper border-[2.5px] border-ink rounded-2xl px-4 py-3 shadow-pop-sm">
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink font-extrabold">
                <Lock size={11} strokeWidth={2.5} /> Pin reveals after RSVP
              </span>
              {event.rough_area && (
                <span className="font-display text-lg leading-tight text-ink">
                  {event.rough_area}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {canSeeExact && event.exact_address && (
        <p
          className={`text-sm text-ink/80 transition-opacity duration-500 ${
            revealed ? "opacity-100" : "opacity-0"
          }`}
        >
          {event.exact_address}
        </p>
      )}
      {canSeeExact && (
        <div className={`flex flex-wrap gap-2 transition-opacity duration-700 ${revealed ? "opacity-100" : "opacity-0"}`}>
          <a
            href={gmaps}
            target="_blank"
            rel="noreferrer"
            data-testid="open-in-google-maps"
            className="cta-primary !py-2.5 !px-4 text-xs"
          >
            <Navigation size={13} strokeWidth={2.5} /> Directions
          </a>
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noreferrer"
            data-testid="open-in-google-maps-static"
            className="cta-ghost !py-2.5 !px-4 text-xs"
          >
            <ExternalLink size={12} strokeWidth={2.2} /> Open in Maps
          </a>
        </div>
      )}

      <style>{`
        /* Editorial chunky pin — animate the inner .dot, not the marker root
           (Mapbox positions the root via transform: translate3d(...)) */
        .inonit-marker {
          position: relative;
          width: 28px; height: 36px;
          display: inline-block;
        }
        .inonit-marker .dot {
          position: absolute; left: 50%; top: 50%;
          width: 22px; height: 22px;
          margin-left: -11px; margin-top: -11px;
          background: ${NEON};
          border: 2.5px solid ${INK};
          border-radius: 9999px;
          box-shadow: 0 2px 0 0 ${INK};
          z-index: 2;
          animation: pin-drop 520ms cubic-bezier(0.34,1.56,0.64,1) both;
        }
        .inonit-marker .ring {
          position: absolute; left: 50%; top: 50%;
          width: 22px; height: 22px;
          margin-left: -11px; margin-top: -11px;
          border-radius: 9999px;
          background: ${NEON};
          opacity: 0.45;
          animation: gentle-pulse 2.4s cubic-bezier(0.22,0.61,0.36,1) infinite;
          z-index: 1;
        }
        @keyframes pin-drop {
          0%   { transform: translateY(-22px) scale(0.7); opacity: 0; }
          60%  { transform: translateY(2px)  scale(1.06); opacity: 1; }
          100% { transform: translateY(0)    scale(1);    opacity: 1; }
        }
        @keyframes gentle-pulse {
          0%   { transform: scale(1);   opacity: 0.45; }
          80%  { transform: scale(2.6); opacity: 0; }
          100% { transform: scale(2.6); opacity: 0; }
        }
        @keyframes shimmer-em {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .animate-shimmer-em { animation: shimmer-em 1.6s linear infinite; }

        /* Editorial control polish (cream + ink, large tap targets) */
        .mapboxgl-ctrl-group {
          background: ${"#FCFAF1"} !important;
          box-shadow: 3px 3px 0 0 ${INK} !important;
          border: 2.5px solid ${INK} !important;
          border-radius: 1rem !important;
          overflow: hidden;
        }
        .mapboxgl-ctrl-group button {
          background-color: transparent !important;
          width: 42px !important;
          height: 42px !important;
        }
        .mapboxgl-ctrl-group button + button { border-top: 2px solid ${INK} !important; }
        .mapboxgl-ctrl-group button:hover { background-color: ${NEON} !important; }
        .mapboxgl-ctrl-icon { filter: none; }
        .mapboxgl-ctrl-attrib {
          background: rgba(244,240,225,0.85) !important;
          color: rgba(10,10,10,0.55) !important;
          border-radius: 0.5rem;
        }
        .mapboxgl-ctrl-attrib a { color: ${INK} !important; }
      `}</style>
    </div>
  );
}

